import dayjs from "dayjs";
import { createSelector } from "@reduxjs/toolkit";
import { IResult } from "redmond/build/common";
import {
  CallState,
  Category,
  ExperiencesAvailabilityAutocompleteGroup,
  ExperiencesAvailabilityAutocompleteResult,
  ViewedExperiencesListProperties,
  ExperienceTag,
  ExperienceTagCount,
  GroupedExperienceIds,
  ITrackingProperties,
  ParsedGroupedExperienceIds,
  PriceRefinement,
  TripadvisorRatingRefinement,
  ActivitiesAvailabilityProperties,
  ActivitiesEntryTypeEnum,
} from "redmond";
import { IStoreState } from "../../../../reducers/types";
import {
  getLocation,
  getFromDate,
  getUntilDate,
  getBaseActivitiesProperties,
  getEntryType,
  getEntryTypesAll,
  getLodgingId,
  getIsEntryTypeCrossSell,
} from "../../../search/reducer";
import {
  computeAvailabilityFilterPills,
  AvailabilityFilterPill,
  AVAILABILITY_FILTER_PILL_CONFIG,
} from "../../availabilityFilterPillConfig";
import { getRewardsAccounts } from "../../../rewards/reducer";

export const getExperiencesAvailabilityCallState = (state: IStoreState) =>
  state.experiencesAvailability.experiencesAvailabilityCallState;

export const getExperiencesAvailabilityResponse = (state: IStoreState) =>
  state.experiencesAvailability.availabilityResponse;

export const getExperiencesAvailabilityNextPageToken = (state: IStoreState) =>
  state.experiencesAvailability.availabilityNextPageToken;

export const getExperiencesAutocompleteResults = (state: IStoreState) =>
  state.experiencesAvailability.autocompleteResults;

export const getExperiences = (state: IStoreState) =>
  state.experiencesAvailability.availabilityExperiences;

export const getExperiencesMap = (state: IStoreState) =>
  state.experiencesAvailability.experiencesMap;

export const getExperiencesMaxPrice = (state: IStoreState) =>
  state.experiencesAvailability.availabilityMaxPrice;

export const getExperiencesMinPrice = (state: IStoreState) =>
  state.experiencesAvailability.availabilityMinPrice;

export const getExperiencesPopularTagsList = createSelector(
  getExperiencesAvailabilityResponse,
  (availabilityResponse): ExperienceTag[] => {
    if (!availabilityResponse) {
      return [];
    }
    return availabilityResponse.popularFeatureTags.map((tag) => {
      return tag.tag;
    });
  },
);

export const getExperiencesByCategory = createSelector(
  getExperiencesAvailabilityResponse,
  (availabilityResponse): GroupedExperienceIds[] => {
    if (!availabilityResponse) {
      return [];
    }

    return availabilityResponse.experiencesByCategory;
  },
);

export const getExperiencesCategoryTags = (state: IStoreState) =>
  state.experiencesAvailability.categoryTags;

/**
 * Get initial category tags from the unfiltered "Best of" request.
 * Used for filter pill display to ensure pinned categories remain visible
 * when filtering by attraction/category.
 */
export const getInitialCategoryTags = (
  state: IStoreState,
): ExperienceTagCount[] => state.experiencesAvailability.initialCategoryTags;

/**
 * Get initial attraction tags from the unfiltered "Best of" request.
 * Used for filter pill display to ensure pinned categories remain visible
 * when filtering by attraction/category.
 */
export const getInitialAttractionTags = (
  state: IStoreState,
): ExperienceTagCount[] => state.experiencesAvailability.initialAttractionTags;

/**
 * Get attraction tags from the availability response.
 * Unlike categoryTags which is a curated subset, attractionTags is the complete
 * list of attraction tags for the searched destination.
 * Note: This returns the current (potentially filtered) attraction tags.
 * For filter pill computation, use getInitialAttractionTags instead.
 */
export const getAttractionTags = createSelector(
  getExperiencesAvailabilityResponse,
  (availabilityResponse): ExperienceTagCount[] => {
    if (!availabilityResponse) {
      return [];
    }
    return availabilityResponse.attractionTags ?? [];
  },
);

/**
 * Get activities grouped by attraction from the availability response.
 */
export const getActivitiesByAttraction = createSelector(
  getExperiencesAvailabilityResponse,
  (availabilityResponse): GroupedExperienceIds[] => {
    if (!availabilityResponse) {
      return [];
    }
    return availabilityResponse.activitiesByAttraction ?? [];
  },
);

/**
 * Computes which pills (attractions vs categories) to display based on
 * the configurable thresholds in PILL_DISPLAY_CONFIG.
 *
 * Uses the initial (unfiltered) attraction and category tags to determine
 * the stable pill LIST, then overlays current (filtered) counts so that
 * pill counts reflect active filters (duration, price, rating, etc.).
 *
 * Logic:
 * - If < 5 qualifying attractions: show only categories
 * - If > 10 qualifying attractions: show only attractions
 * - If 5-10 qualifying attractions: show mix (attractions first, then categories)
 * - An attraction qualifies if it has >= 20 products
 */
export const getAvailabilityFilterPills = createSelector(
  getInitialAttractionTags,
  getInitialCategoryTags,
  getExperiencesCategoryTags,
  getAttractionTags,
  (
    initialAttractionTags,
    initialCategoryTags,
    currentCategoryTags,
    currentAttractionTags,
  ): AvailabilityFilterPill[] => {
    // Compute which pills to show based on initial (stable) tags
    const pills = computeAvailabilityFilterPills(
      initialAttractionTags,
      initialCategoryTags,
      AVAILABILITY_FILTER_PILL_CONFIG,
    );

    // No filtered data yet (pre-first-load) — return with initial counts
    if (
      currentCategoryTags.length === 0 &&
      currentAttractionTags.length === 0
    ) {
      return pills;
    }

    // Build lookups from current (filtered) counts
    const currentCategoryCounts = new Map(
      currentCategoryTags.map((t) => [t.tag.id, t.count]),
    );
    const currentAttractionCounts = new Map(
      currentAttractionTags.map((t) => [t.tag.id, t.count]),
    );

    // Overlay filtered counts onto stable pill list.
    // Use Math.min of the filtered count and the initial (corrected) count:
    // - pill.tagCount.count comes from initialCategoryTags, which persists
    //   actual loaded counts discovered when a category was fully fetched
    // - filteredCount comes from the latest API response's categoryTags,
    //   reflecting active filters (duration, price, rating, etc.)
    // Math.min ensures both corrections and filter reductions are respected.
    return pills
      .map((pill) => {
        const countMap =
          pill.type === "Attraction"
            ? currentAttractionCounts
            : currentCategoryCounts;
        const filteredCount = countMap.get(pill.tagCount.tag.id);

        if (filteredCount !== undefined) {
          const count = Math.min(filteredCount, pill.tagCount.count);
          return {
            ...pill,
            tagCount: { ...pill.tagCount, count },
          };
        }
        // Not in filtered response — keep initial/corrected count
        return pill;
      })
      .filter((pill) => pill.tagCount.count > 0);
  },
);

export const getExperiencesTags = createSelector(
  getExperiencesAvailabilityResponse,
  (availabilityResponse): ExperienceTagCount[] => {
    if (!availabilityResponse) {
      return [];
    }

    return availabilityResponse.tags;
  },
);

export const getParsedExperiencesByCategory = createSelector(
  getExperiencesByCategory,
  getExperiencesTags,
  (experiencesByCategory, tags): ParsedGroupedExperienceIds[] => {
    if (experiencesByCategory.length <= 0 || tags.length <= 0) {
      return [];
    }

    return experiencesByCategory.map((item) => {
      const matchingCategoryTag = tags.find((categoryTag) => {
        return categoryTag.tag.id === item.tag.id;
      });
      return {
        tag: matchingCategoryTag ?? { tag: item.tag, count: 0 },
        experienceIds: item.experienceIds,
      };
    });
  },
);

export const getExistingExperiencesAvailabilityRequestParameters =
  createSelector(
    getLocation,
    getFromDate,
    getUntilDate,

    (
      location,
      departureDate,
      returnDate,
    ): {
      location: IResult | null;
      departureDate: Date | null;
      returnDate: Date | null;
    } => ({
      location,
      departureDate,
      returnDate,
    }),
  );

export const getExperiencesQueryParams = createSelector(
  getFromDate,
  getUntilDate,
  (fromDate, untilDate) => ({
    fromDate,
    untilDate,
  }),
);

export const getKeyword = (
  state: IStoreState,
): ExperiencesAvailabilityAutocompleteResult | null =>
  state.experiencesAvailability.keyword;

export const getKeywordCategories = createSelector(
  getExperiencesAutocompleteResults,
  getInitialCategoryTags,
  getInitialAttractionTags,
  (
    autocompleteResults,
    availabilityCategoryTags,
    availabilityAttractionTags,
  ): ExperiencesAvailabilityAutocompleteGroup[] => {
    if (!autocompleteResults) {
      return [];
    }

    // Build lookup maps from availability response tags (which have real counts)
    const categoryCountById = new Map(
      availabilityCategoryTags.map((t) => [t.tag.id, t.count]),
    );
    const attractionCountById = new Map(
      availabilityAttractionTags.map((t) => [t.tag.id, t.count]),
    );

    let experiencesAvailabilityAutocompleteGroups: ExperiencesAvailabilityAutocompleteGroup[] =
      [];

    // Attractions group first (prioritized above categories)
    if (autocompleteResults.attractions?.length) {
      const seen = new Set<number>();
      const attractionResults: ExperiencesAvailabilityAutocompleteResult[] =
        autocompleteResults.attractions
          .filter((r) => {
            // Deduplicate by tag ID
            if (seen.has(r.tag.id)) return false;
            seen.add(r.tag.id);
            return true;
          })
          .reduce<ExperiencesAvailabilityAutocompleteResult[]>(
            (acc, attractionResult) => {
              // Use availability response count if autocomplete returned 0
              const count =
                attractionResult.count ||
                attractionCountById.get(attractionResult.tag.id) ||
                0;
              // Hide attractions with zero activity results
              if (count > 0) {
                acc.push({
                  id: attractionResult.tag.id.toString(),
                  label: `${attractionResult.tag.name} (${count})`,
                });
              }
              return acc;
            },
            [],
          );

      if (attractionResults.length > 0) {
        // Sort by curated attraction order from availability response
        const attractionPositionMap = new Map(
          availabilityAttractionTags.map((t, i) => [t.tag.id, i]),
        );
        attractionResults.sort(
          (a, b) =>
            (attractionPositionMap.get(parseInt(a.id)) ?? Infinity) -
            (attractionPositionMap.get(parseInt(b.id)) ?? Infinity),
        );

        experiencesAvailabilityAutocompleteGroups.push({
          category: Category.Attractions,
          label: "Attractions",
          results: attractionResults,
        });
      }
    }

    const seen = new Set<number>();
    const categoryTagResults: ExperiencesAvailabilityAutocompleteResult[] =
      autocompleteResults.categories
        .filter((r) => {
          // Deduplicate by tag ID
          const id = r.tag.id;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .reduce<ExperiencesAvailabilityAutocompleteResult[]>(
          (acc, categoryResult) => {
            // Use availability response count if autocomplete returned 0
            const count =
              categoryResult.count ||
              categoryCountById.get(categoryResult.tag.id) ||
              0;
            // Hide categories with zero results
            if (count > 0) {
              acc.push({
                id: categoryResult.tag.id.toString(),
                label: `${categoryResult.tag.name} (${count})`,
              });
            }
            return acc;
          },
          [],
        );

    if (categoryTagResults.length > 0) {
      experiencesAvailabilityAutocompleteGroups.push({
        category: Category.Categories,
        label: "Categories",
        results: categoryTagResults,
      });
    }

    const specificExperiencesResults: ExperiencesAvailabilityAutocompleteResult[] =
      autocompleteResults.experiences.map((experiencesResult) => {
        return {
          id: experiencesResult.id.value,
          label: experiencesResult.name,
          rating: experiencesResult.rating,
        };
      });

    experiencesAvailabilityAutocompleteGroups.push({
      category: Category.Experiences,
      label: "Activities",
      results: specificExperiencesResults,
    });

    return experiencesAvailabilityAutocompleteGroups;
  },
);

export const getKeywordCategoriesLoading = (state: IStoreState) =>
  state.experiencesAvailability.autocompleteCallState === CallState.InProcess;

export const getFilteredTripAdvisorRating = (state: IStoreState) =>
  state.experiencesAvailability.tripAdvisorRating
    ? state.experiencesAvailability.tripAdvisorRating
    : TripadvisorRatingRefinement.Any;

export const getFilteredStartTimes = (state: IStoreState) =>
  state.experiencesAvailability.startTime
    ? state.experiencesAvailability.startTime
    : [];

export const getFilteredDurationTimes = (state: IStoreState) =>
  state.experiencesAvailability.duration
    ? state.experiencesAvailability.duration
    : [];

export const getEncodedTags = createSelector(
  getExperiencesAvailabilityResponse,
  (availabilityResponse): string | undefined => {
    if (!availabilityResponse) {
      return undefined;
    }
    return availabilityResponse.encodedTags;
  },
);

export const getFilterPrice = (state: IStoreState) =>
  state.experiencesAvailability.price;

export const getCurrentFilterPrice = createSelector(
  getExperiencesMaxPrice,
  getFilterPrice,
  (experiencesMaxPrice, filterPrice): PriceRefinement => {
    return filterPrice
      ? filterPrice
      : experiencesMaxPrice
      ? { maxPriceUSD: experiencesMaxPrice.fiat.value }
      : { maxPriceUSD: 0 };
  },
);

export const getFilteredTags = (state: IStoreState) =>
  state.experiencesAvailability.tags ? state.experiencesAvailability.tags : [];

export const getFilteredPopularFeatures = (state: IStoreState) =>
  state.experiencesAvailability.popularFeatures
    ? state.experiencesAvailability.popularFeatures
    : [];

export const getFiltersChangedSinceLastSearch = (state: IStoreState) =>
  state.experiencesAvailability.filtersChangedSinceLastSearch;

export const getPriceFiltersChangedSinceLastSearch = (state: IStoreState) =>
  state.experiencesAvailability.priceFiltersChangedSinceLastSearch;

export const getAllAppliedFiltersList = createSelector(
  getFilteredTripAdvisorRating,
  getFilteredStartTimes,
  getFilteredDurationTimes,
  getFilterPrice,
  getFilteredTags,
  getFilteredPopularFeatures,
  (
    tripAdvisorRating,
    startTimes,
    durationTimes,
    price,
    tags,
    popularFeatures,
  ) => {
    const appliedFilters = [];

    if (tripAdvisorRating) {
      appliedFilters.push("tripAdvisor");
    }
    if (startTimes.length > 0) {
      appliedFilters.push("startTimes");
    }
    if (durationTimes.length > 0) {
      appliedFilters.push("durationTimes");
    }
    if (price) {
      appliedFilters.push("price");
    }
    if (tags.length > 0) {
      appliedFilters.push("tags");
    }
    if (popularFeatures.length > 0) {
      appliedFilters.push("popularFeatures");
    }

    return appliedFilters;
  },
);
export const getSortOption = (state: IStoreState) =>
  state.experiencesAvailability.sort;

export const getExperiencesByCategoryMap = (state: IStoreState) =>
  state.experiencesAvailability.experiencesByCategoryMap;

export const getSelectedGroupedExperience = (state: IStoreState) =>
  state.experiencesAvailability.selectedGroupedExperience;

export const getCrossSellProperties = createSelector(
  getIsEntryTypeCrossSell,
  getEntryType,
  (isFromCrossSell, entryType) => {
    if (!isFromCrossSell || entryType == null) return {};

    const getOriginProduct = () => {
      switch (entryType) {
        case ActivitiesEntryTypeEnum.ACTIVITIES_XSELL:
        case ActivitiesEntryTypeEnum.ACTIVITIES_XSELL_CARD:
          return "activity";
        case ActivitiesEntryTypeEnum.STAYS_XSELL:
        case ActivitiesEntryTypeEnum.STAYS_XSELL_CARD:
          return "stay";
        default:
          return undefined;
      }
    };

    const getClickTarget = () => {
      if (
        [
          ActivitiesEntryTypeEnum.ACTIVITIES_XSELL,
          ActivitiesEntryTypeEnum.STAYS_XSELL,
        ].includes(entryType)
      ) {
        return "view_all";
      }
      if (
        [
          ActivitiesEntryTypeEnum.ACTIVITIES_XSELL_CARD,
          ActivitiesEntryTypeEnum.STAYS_XSELL_CARD,
        ].includes(entryType)
      ) {
        return "carousel_card";
      }

      return undefined;
    };

    return {
      feed_placement: "booking_confirmation", // x-sell is only on activities/stays confirmation currently
      origin_product_type: getOriginProduct(),
      xsell_click_target: getClickTarget(),
    };
  },
);
export const getActivitiesAvailabilityProperties = createSelector(
  getBaseActivitiesProperties,
  getLocation,
  getFromDate,
  getAllAppliedFiltersList,
  getSelectedGroupedExperience,
  getEntryType,
  getEntryTypesAll,
  getLodgingId,
  getIsEntryTypeCrossSell,
  getCrossSellProperties,
  (
    baseActivitiesProperties,
    locationSearched,
    fromDate,
    appliedFiltersList,
    selectedGroupedExperience,
    entryType,
    entryTypesAll,
    lodgingId,
    isFromCrossSell,
    crossSellProperties,
  ): ITrackingProperties<ActivitiesAvailabilityProperties> => ({
    properties: {
      ...baseActivitiesProperties?.properties,
      advance: dayjs(fromDate).diff(dayjs(), "days"),
      location_searched: locationSearched?.label,
      ...locationSearched?.trackingPropertiesV2?.properties,
      filters_applied: appliedFiltersList,
      tag_selected: selectedGroupedExperience?.tag.name,
      entry_type: isFromCrossSell ? "cross_sell" : entryType,
      entry_types_all: entryTypesAll,
      lodging_id: lodgingId ?? undefined,
      ...crossSellProperties,
    },
    encryptedProperties: locationSearched?.trackingPropertiesV2
      ?.encryptedProperties
      ? [locationSearched?.trackingPropertiesV2?.encryptedProperties]
      : [],
  }),
);

// TODO: Remove this as part of activities tracking clean up
export const getViewedExperiencesListProperties = createSelector(
  getRewardsAccounts,
  getLocation,
  (
    rewardsAccounts,
    locationSearched,
  ): ITrackingProperties<ViewedExperiencesListProperties> => ({
    properties: {
      rewards_accounts: rewardsAccounts
        .map((r) => r.productDisplayName)
        .join(","),
      ...locationSearched?.trackingPropertiesV2?.properties,
    },
    encryptedProperties: locationSearched?.trackingPropertiesV2
      ?.encryptedProperties
      ? [locationSearched?.trackingPropertiesV2?.encryptedProperties]
      : [],
  }),
);

export const getUserHasBookedActivities = (state: IStoreState) =>
  state.experiencesAvailability.userHasActivitiesBooked;

export const getViewPreference = (state: IStoreState) =>
  state.experiencesAvailability.viewPreference;

export const getSelectedAttraction = (state: IStoreState) =>
  state.experiencesAvailability.selectedAttraction;
